import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Document, Page, pdfjs } from "react-pdf";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Download, FileText, Loader2, BookOpen, Newspaper, Images } from "lucide-react";
import { Button } from "../components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  "pdfjs-dist/build/pdf.worker.min.mjs",
  import.meta.url
).toString();

const resolvePublicFileUrl = (url) => {
  if (!url) return "";
  // Reescribir SIEMPRE al backend actual (URLs absolutas de dominios anteriores rompen el visor por CORS)
  const filesIdx = url.indexOf("/api/informaciones/files/");
  if (filesIdx !== -1) return `${API}/public/files/${url.split("/").pop()}`;
  const apiIdx = url.indexOf("/api/");
  if (apiIdx !== -1) return `${API}${url.slice(apiIdx + 4)}`;
  if (url.startsWith("http")) return url;
  return url;
};

// PDF Viewer for public page
function PublicPdfViewer({ url, title }) {
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1.3);
  const [loadError, setLoadError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [blobUrl, setBlobUrl] = useState(null);

  useEffect(() => {
    if (!url) return;
    fetch(url)
      .then(r => { if (!r.ok) throw new Error(); return r.blob(); })
      .then(blob => setBlobUrl(URL.createObjectURL(blob)))
      .catch(() => setLoadError(true));
    return () => { if (blobUrl) URL.revokeObjectURL(blobUrl); };
  }, [url]);

  if (loadError) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-slate-500">
        <FileText className="w-16 h-16 text-slate-300 mb-4" />
        <p className="text-lg font-medium">No se pudo cargar el documento</p>
        <a href={url} download className="mt-4">
          <Button variant="outline"><Download className="w-4 h-4 mr-2" /> Descargar</Button>
        </a>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center">
      {/* Controls */}
      <div className="sticky top-0 z-10 bg-white/90 backdrop-blur border-b w-full flex items-center justify-center gap-3 py-2 px-4">
        {numPages && numPages > 1 && (
          <div className="flex items-center gap-1">
            <Button variant="ghost" size="sm" disabled={pageNumber <= 1} onClick={() => setPageNumber(p => p - 1)}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-sm text-slate-600 min-w-[60px] text-center">{pageNumber} / {numPages}</span>
            <Button variant="ghost" size="sm" disabled={pageNumber >= numPages} onClick={() => setPageNumber(p => p + 1)}>
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        )}
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" onClick={() => setScale(s => Math.max(0.5, s - 0.2))}>
            <ZoomOut className="w-4 h-4" />
          </Button>
          <span className="text-xs text-slate-500 min-w-[40px] text-center">{Math.round(scale * 100)}%</span>
          <Button variant="ghost" size="sm" onClick={() => setScale(s => Math.min(3, s + 0.2))}>
            <ZoomIn className="w-4 h-4" />
          </Button>
        </div>
        <a href={url} download>
          <Button variant="outline" size="sm"><Download className="w-4 h-4 mr-1" /> Descargar</Button>
        </a>
      </div>
      {/* PDF */}
      <div className="py-6">
        {loading && !blobUrl && (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
            <span className="ml-3 text-slate-500">Cargando documento...</span>
          </div>
        )}
        {blobUrl && (
          <Document
            file={blobUrl}
            onLoadSuccess={({ numPages: n }) => { setNumPages(n); setLoading(false); }}
            onLoadError={() => setLoadError(true)}
            loading={null}
          >
            <Page pageNumber={pageNumber} scale={scale} renderTextLayer renderAnnotationLayer className="shadow-lg" />
          </Document>
        )}
      </div>
    </div>
  );
}

export default function PublicContentPage() {
  const { token } = useParams();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch(`${API}/public/info/${token}`)
      .then(r => { if (!r.ok) throw new Error("not_found"); return r.json(); })
      .then(data => { setContent(data); setLoading(false); })
      .catch(() => { setError("El enlace no es válido o el contenido ya no está disponible."); setLoading(false); });
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-10 h-10 animate-spin text-teal-600" />
        <span className="ml-4 text-slate-500 text-lg">Cargando contenido...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex flex-col items-center justify-center px-4">
        <FileText className="w-20 h-20 text-slate-300 mb-6" />
        <h1 className="text-2xl font-bold text-slate-700 mb-2">Contenido no disponible</h1>
        <p className="text-slate-500 text-center max-w-md">{error}</p>
      </div>
    );
  }

  const { type, data } = content;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Minimal header */}
      <header className="bg-white border-b px-6 py-3 flex items-center gap-3 shadow-sm">
        <div className="w-8 h-8 rounded-lg bg-teal-600 flex items-center justify-center">
          {type === "documento" && <FileText className="w-4 h-4 text-white" />}
          {type === "noticia" && <Newspaper className="w-4 h-4 text-white" />}
          {type === "libro" && <BookOpen className="w-4 h-4 text-white" />}
          {type === "album" && <Images className="w-4 h-4 text-white" />}
        </div>
        <span className="text-sm font-semibold text-slate-700">QAsesores</span>
        <span className="text-slate-300">|</span>
        <span className="text-sm text-slate-500 capitalize">{type === "documento" ? "Documento" : type === "noticia" ? "Noticia" : type === "libro" ? "Lectura" : "Galería"}</span>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {type === "documento" && <DocumentoPublic data={data} />}
        {type === "noticia" && <NoticiaPublic data={data} />}
        {type === "libro" && <LibroPublic data={data} />}
        {type === "album" && <AlbumPublic data={data} />}
      </main>
    </div>
  );
}

function DocumentoPublic({ data }) {
  const isPdf = data.tipo_archivo === "PDF" || (data.url && data.url.toLowerCase().endsWith(".pdf"));
  const isHtml = data.tipo === "html";
  const fileUrl = resolvePublicFileUrl(data.url);

  return (
    <div>
      <h1 className="text-2xl font-bold text-slate-800 mb-2" data-testid="public-doc-title">{data.titulo}</h1>
      {data.descripcion && <p className="text-slate-500 mb-6">{data.descripcion}</p>}

      {isHtml && data.contenido_html ? (
        <div className="bg-white rounded-xl shadow-sm border p-8 prose prose-slate max-w-none" dangerouslySetInnerHTML={{ __html: data.contenido_html }} />
      ) : isPdf && fileUrl ? (
        <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
          <PublicPdfViewer url={fileUrl} title={data.titulo} />
        </div>
      ) : fileUrl ? (
        <div className="bg-white rounded-xl shadow-sm border p-8 text-center">
          <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-600 mb-4">Este tipo de archivo no se puede previsualizar.</p>
          <a href={fileUrl} download>
            <Button><Download className="w-4 h-4 mr-2" /> Descargar {data.nombre_archivo || "archivo"}</Button>
          </a>
        </div>
      ) : (
        <p className="text-slate-500">No hay contenido disponible para este documento.</p>
      )}
    </div>
  );
}

function NoticiaPublic({ data }) {
  return (
    <article>
      <h1 className="text-2xl font-bold text-slate-800 mb-2" data-testid="public-news-title">{data.titulo}</h1>
      <div className="flex items-center gap-3 text-sm text-slate-400 mb-6">
        {data.autor_nombre && <span>Por {data.autor_nombre}</span>}
        {data.created_at && <span>{new Date(data.created_at).toLocaleDateString("es-DO", { year: "numeric", month: "long", day: "numeric" })}</span>}
      </div>
      {data.imagen_url && (
        <img src={resolvePublicFileUrl(data.imagen_url)} alt="" className="w-full rounded-xl mb-6 max-h-96 object-cover" />
      )}
      {data.contenido ? (
        <div className="bg-white rounded-xl shadow-sm border p-8 prose prose-slate max-w-none" dangerouslySetInnerHTML={{ __html: data.contenido }} />
      ) : (
        <p className="text-slate-500">{data.resumen || "Sin contenido."}</p>
      )}
    </article>
  );
}

function LibroPublic({ data }) {
  return (
    <div>
      <div className="flex items-start gap-6 mb-6">
        {data.portada_url && (
          <img src={resolvePublicFileUrl(data.portada_url)} alt="" className="w-32 rounded-lg shadow" />
        )}
        <div>
          <h1 className="text-2xl font-bold text-slate-800" data-testid="public-book-title">{data.titulo}</h1>
          {data.autor && <p className="text-slate-500 mt-1">Por {data.autor}</p>}
          {data.descripcion && <p className="text-slate-400 mt-2 text-sm">{data.descripcion}</p>}
        </div>
      </div>
      {data.contenido_html ? (
        <div className="bg-white rounded-xl shadow-sm border p-8 prose prose-slate max-w-none" dangerouslySetInnerHTML={{ __html: data.contenido_html }} />
      ) : data.pdf_url ? (
        <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
          <PublicPdfViewer url={resolvePublicFileUrl(data.pdf_url)} title={data.titulo} />
        </div>
      ) : data.enlace_externo ? (
        <div className="bg-white rounded-xl shadow-sm border p-8 text-center">
          <a href={data.enlace_externo} target="_blank" rel="noopener noreferrer">
            <Button><BookOpen className="w-4 h-4 mr-2" /> Abrir enlace externo</Button>
          </a>
        </div>
      ) : (
        <p className="text-slate-500">No hay contenido disponible.</p>
      )}
    </div>
  );
}

function AlbumPublic({ data }) {
  const fotos = data.fotos || [];
  return (
    <div>
      <h1 className="text-2xl font-bold text-slate-800 mb-2" data-testid="public-album-title">{data.titulo || data.nombre}</h1>
      {data.descripcion && <p className="text-slate-500 mb-6">{data.descripcion}</p>}
      {fotos.length > 0 ? (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {fotos.map((foto, i) => (
            <div key={foto.id || i} className="aspect-square rounded-xl overflow-hidden shadow-sm border bg-white">
              <img
                src={resolvePublicFileUrl(foto.url || foto.imagen_url)}
                alt={foto.titulo || foto.descripcion || ""}
                className="w-full h-full object-cover"
              />
            </div>
          ))}
        </div>
      ) : (
        <p className="text-slate-500">No hay fotos en este álbum.</p>
      )}
    </div>
  );
}
